import Link from "next/link";
import { useState } from "react";
import { BsPersonVcardFill } from "react-icons/bs";
import { FaVideo } from "react-icons/fa";
import { Product } from "../../../graphql/operations";
import { formatDate } from "../../../utils/calendarUtils";
import { CloudinaryImage } from "../../Atoms/Cloudinary";
import GuideProfileVideo from "../../Atoms/GuideProfileVideo";
import ModalScreen from "../../Atoms/ModalScreen";
import TruncatedText from "../../Atoms/TruncatedText";
import styles from "./GuideAvatarProfile.module.scss";

interface GuideAvatarProfileProps {
  timeSlot: { time: string };
  productAvailability: { date: string };
  guide?: {
    id: string;
    nickName: string;
    profilePic: string;
    profileVideo?: string;
    metrics: {
      numTours: number;
      numPeople: number;
    };
    translation: { description: string };
  };
  product: Pick<Product, "id">;
}

export default function GuideAvatarProfile({
  timeSlot,
  productAvailability,
  guide,
  product,
}: GuideAvatarProfileProps) {
  const [state, setState] = useState({ isOpen: false });

  const handlePlayVideo = () => {
    setState({ isOpen: true });
  };

  const checkoutUrl = ({ product, guideId, date, time }) => {
    const urlSearchParams = new URLSearchParams({
      date: formatDate(date),
      time: time,
      guideId: guideId,
    });

    return `/checkout/${
      product.id
    }?${urlSearchParams.toString()}&ref=guide-carousel`;
  };

  if (!guide?.profilePic || !guide?.nickName) return;

  return (
    <div className={styles.GuideAvatarProfileWrapper}>
      <div className={styles.GuideAvatarProfileImgContainer}>
        <CloudinaryImage
          src={guide.profilePic}
          transformations={[`w_280`, `h_250`, "c_fill", "e_art:zorro"]}
        />
        <strong>{guide.nickName}</strong>
        <div className={styles.GuideAvatarIcons}>
          {guide.profileVideo && (
            <i onClick={handlePlayVideo}>
              <FaVideo size={26} color={"#fff"} />
            </i>
          )}
          <a href={`/guide-profile/${guide.id}`} target="_blank">
            <BsPersonVcardFill size={28} color="#fff" />
          </a>
        </div>
      </div>
      <div className={styles.GuideAvatarProfileContents}>
        <div className={styles.GuideAvatarProfileAbout}>
          <h5>About</h5>
          {guide.translation.description ? (
            <p>
              <TruncatedText
                text={guide.translation?.description}
                maxLength={110}
              />
              {guide.translation?.description.length > 110 && (
                <>
                  {" "}
                  <a href={`/guide-profile/${guide.id}`} target="_blank">
                    Read more
                  </a>
                </>
              )}
            </p>
          ) : (
            <p>Hi I am {guide.nickName}!</p>
          )}
        </div>

        <div className={styles.GuideAvatarProfileStats}>
          <div className={styles.GuideStatInfo}>
            <b>{guide.metrics.numTours}</b>
            <span>Tours Guided</span>
          </div>
          <div className={styles.GuideStatInfo}>
            <b>{guide.metrics.numPeople}</b>
            <span>Guests</span>
          </div>
        </div>
      </div>

      {productAvailability && (
        <div className={styles.GuideAssignedTimeInfo}>
          <Link
            href={checkoutUrl({
              product,
              date: productAvailability.date,
              time: timeSlot.time,
              guideId: guide.id,
            })}
          >
            <b>Tour Date & Time</b>
            <span>
              {productAvailability.date} {timeSlot.time}
            </span>
          </Link>
        </div>
      )}

      <ModalScreen
        transparent
        openVideo={state.isOpen}
        onClose={() => {
          setState({ isOpen: false });
        }}
      >
        <GuideProfileVideo guideId={guide.id} publicId={guide.profileVideo} />
      </ModalScreen>
    </div>
  );
}
